import { useDeviceType } from '@cian/ui-kit/responsive/hooks/useDeviceType';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cian/frontend-reviews-components';
import { IconSocialDislikeOn16, IconSocialLikeOn16 } from '@cian/ui-kit-design-tokens/icons';

import { ButtonLink } from '../../../components/ButtonLink';
import { Footer } from '../../../components/Footer';
import { EStatus } from '../../../types/likes';
import { NewAnswerButtonContainer } from '../../NewAnswerButton';
import { getQuestionById } from '../../../selectors/questions';
import { QuestionPopupContainer } from '../../QuestionPopup';
import { setAnswersVisibility, setQuestionReaction } from '../../../actions/questions';
import { loadQuestionAnswers } from '../../../actions/questionAnswers';
import { requestAuthentication } from '../../../actions/authentication';
import { getIsAuthenticated } from '../../../selectors/user';
import { trackOpenThread } from '../tracking';

interface IFooterContainerProps {
  questionId: string;
  inPopup?: boolean;
}

export function FooterContainer({ questionId, inPopup }: IFooterContainerProps): React.JSX.Element {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const [reviewPopupId, setReviewPopupId] = React.useState<string | null>(null);
  const { like, answersCount, areAnswersOpened, hasAnswers } = useSelector(getQuestionById(questionId));
  const { answers } = useSelector(getQuestionById(questionId));

  const onReviewPopupClose = () => setReviewPopupId(null);

  const setLike = React.useCallback(() => setQuestionReaction({ questionId, type: EStatus.Liked }), []);
  const setDisliked = React.useCallback(() => setQuestionReaction({ questionId, type: EStatus.Disliked }), []);

  const onLikeClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setLike());
    } else {
      dispatch(requestAuthentication({ onSuccess: setLike }));
    }
  }, [dispatch, isAuthenticated, setLike]);

  const onDislikedClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setDisliked());
    } else {
      dispatch(requestAuthentication({ onSuccess: setDisliked }));
    }
  }, [dispatch, isAuthenticated, setDisliked]);

  const onAnswerVisibilityClick = React.useCallback(() => {
    if (!areAnswersOpened || !isDesktop) {
      const page = {
        siteType: deviceType,
        offerType: 'JK',
        pageType: 'ReviewsJK',
        extra: {
          question_id: questionId,
        },
      };

      // Обернуто в setTimeout для улучшения INP
      setTimeout(() => trackOpenThread(page), 0);
    }

    if (isDesktop) {
      dispatch(setAnswersVisibility({ questionId, state: !areAnswersOpened }));
      if (!areAnswersOpened && !answers.length) {
        dispatch(loadQuestionAnswers({ questionId, page: 0 }));
      }
    } else {
      setReviewPopupId(questionId);
    }
  }, [dispatch, areAnswersOpened, questionId, isDesktop]);

  return (
    <>
      <Footer
        like={
          <IconButton
            Icon={IconSocialLikeOn16}
            active={like.status === EStatus.Liked}
            onClick={onLikeClick}
            label={like.likesCount}
          />
        }
        dislike={
          <IconButton
            Icon={IconSocialDislikeOn16}
            active={like.status === EStatus.Disliked}
            onClick={onDislikedClick}
            label={like.dislikesCount}
          />
        }
        answer={<NewAnswerButtonContainer type="question" targetId={questionId} />}
        answerVisibility={
          !inPopup &&
          hasAnswers && (
            <ButtonLink onClick={onAnswerVisibilityClick} testId="QuestionAnswerVisibilityControl">
              {isDesktop && areAnswersOpened ? 'Скрыть ответы' : answersCount}
            </ButtonLink>
          )
        }
      />
      {reviewPopupId !== null && <QuestionPopupContainer questionId={reviewPopupId} onClose={onReviewPopupClose} />}
    </>
  );
}
